import {
  Option,
  QuestionModel,
} from '@/modules/core/domain/interfaces/IFormulary';
import { useSurveyForm } from '@/modules/SurveyForm';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDynamicOptions } from '../../context/DynamicOptionsContext';

interface UseNestedFilterOptionsResult {
  options: Option[] | null;
  isDynamic: boolean;
  loading: boolean;
}

function normalizeToArray(value: any): string[] {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value.map(String) : [String(value)];
}

/**
 * Popula as options de uma pergunta "checkbox" dinamicamente, refazendo a
 * busca (GET /v1/exports/nested_filter) sempre que alguma das perguntas
 * listadas em `question.filterBy` mudar de valor. Quando a pergunta não
 * possui `filterBy` (ou nenhum fetcher foi provido), `isDynamic` é `false`
 * e o consumidor deve usar as `options` estáticas do próprio formulário.
 */
export function useNestedFilterOptions(
  question: QuestionModel,
  onChange: (value: any) => void,
): UseNestedFilterOptionsResult {
  const { getValues, subscribe } = useSurveyForm();
  const { fetchOptions } = useDynamicOptions();

  const filterByKeys = useMemo(() => question.filterBy || [], [
    question.filterBy,
  ]);
  const isDynamic = filterByKeys.length > 0 && !!fetchOptions;

  const [options, setOptions] = useState<Option[] | null>(null);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!isDynamic || !fetchOptions) return;

    const filterBy: Record<string, string[]> = {};
    filterByKeys.forEach((key) => {
      filterBy[key] = normalizeToArray(getValues(key)?.values);
    });

    setLoading(true);
    try {
      const result = await fetchOptions({
        filterQuestionId: question.id,
        filterBy,
      });
      setOptions(result);

      const currentSelection = normalizeToArray(getValues(question.id)?.values);
      if (currentSelection.length) {
        const validIds = new Set(result.map((option) => String(option.id)));
        const filtered = currentSelection.filter((id) => validIds.has(id));
        if (filtered.length !== currentSelection.length) {
          onChange(filtered);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isDynamic, fetchOptions, filterByKeys, question.id, getValues, onChange]);

  useEffect(() => {
    if (!isDynamic) return undefined;
    const unsubs = filterByKeys.map((key) => subscribe(key, refetch));
    return () => {
      unsubs.forEach((unsub) => unsub && unsub());
    };
  }, [isDynamic, filterByKeys, subscribe, refetch]);

  return { options, isDynamic, loading };
}
